import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { IDLE_EXPRESSIONS } from "../../constants/emotions";
import ChatErrorBoundary from "./ChatErrorBoundary";
import ChatLauncherButton from "../askpurple/ChatLauncherButton";
import ChatHeader from "../askpurple/ChatHeader";
import ChatMessageList from "../askpurple/ChatMessageList";
import ChatComposer from "../askpurple/ChatComposer";
import WidgetOnboardingForm from "../askpurple/WidgetOnboardingForm";
import { useAskPurpleChat } from "../../hooks";

const MASCOT_SRC = "/askpurple-mark.png";

const welcomeForExisting = (name) =>
  name
    ? `Welcome back, ${name}! Ask me anything about admissions, counselling, or tutoring.`
    : "Welcome back! Ask me anything about admissions, counselling, or tutoring.";

const welcomeForNew = (name) =>
  name
    ? `Hi ${name}! Great to meet you. Based on what you shared, I'll point you to the right answers and resources. What's your first question?`
    : "Great — let's get started. Ask me your first question and I'll help guide you.";

export default function AskPurpleWidget() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const {
    profile,
    onboarded,
    messages,
    input,
    setInput,
    loading,
    mascotState,
    scrollRef,
    handleOnboardDone,
    sendMessage,
    resetProfile,
  } = useAskPurpleChat({
    welcomeForExisting,
    welcomeForNew,
    idleExpressions: IDLE_EXPRESSIONS,
    idleIntervalMs: 6000,
  });

  // Global event listener to open chatbot from any button/nav item across the site
  useEffect(() => {
    const handleOpen = (e) => {
      setOpen(true);
      const prompt = e?.detail?.prompt;
      if (prompt) {
        setInput(prompt);
        if (onboarded) {
          sendMessage(prompt);
        }
      }
    };

    window.addEventListener("open-askpurple", handleOpen);
    window.openAskPurple = (prompt) => {
      window.dispatchEvent(new CustomEvent("open-askpurple", { detail: { prompt } }));
    };

    return () => {
      window.removeEventListener("open-askpurple", handleOpen);
      delete window.openAskPurple;
    };
  }, [setInput, onboarded, sendMessage]);

  // Check URL query parameters (e.g. /?askpurple=true)
  useEffect(() => {
    if (location.search.includes("askpurple=true") || location.state?.openChat) {
      setOpen(true);
    }
  }, [location.search, location.state]);

  // Wrapper for onboarding completion that also auto-dispatches any pending prompt
  const onOnboardComplete = (prof) => {
    handleOnboardDone(prof);
    if (input && input.trim()) {
      const pendingText = input.trim();
      setTimeout(() => {
        sendMessage(pendingText);
      }, 300);
    }
  };

  return (
    <>
      <ChatLauncherButton
        open={open}
        onToggle={() => setOpen((v) => !v)}
        mascotSrc={MASCOT_SRC}
      />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-20 right-4 sm:right-6 z-50 flex h-[560px] max-h-[calc(100dvh-6.5rem)] w-[calc(100vw-2rem)] sm:w-[420px] max-w-md flex-col overflow-hidden rounded-3xl border border-purple-200/80 bg-white shadow-2xl shadow-purple-900/25 ring-1 ring-purple-100"
            data-testid="askpurple-panel"
          >
            <ChatHeader
              loading={loading}
              mascotState={mascotState}
              onboarded={onboarded}
              profileName={profile?.name}
              mascotSrc={MASCOT_SRC}
              onClose={() => setOpen(false)}
              onReset={resetProfile}
            />

            {!onboarded ? (
              <WidgetOnboardingForm onDone={onOnboardComplete} />
            ) : (
              <ChatErrorBoundary>
                <ChatMessageList
                  ref={scrollRef}
                  messages={messages}
                  loading={loading}
                  onSuggestionClick={(s) => sendMessage(s)}
                />
                <ChatComposer
                  input={input}
                  setInput={setInput}
                  onSend={(customText) => sendMessage(customText)}
                  onQuickPrompt={(text) => sendMessage(text)}
                  loading={loading}
                />
              </ChatErrorBoundary>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
